/* AgentMeeting — TopBar (cross-page navigation, used by app/* pages) */
import { CostMeter, RoleSwitcher, Role } from './ui';

export type RoomLayout = 'chat-stream' | 'round-table' | 'arena';

export type MeetingHeader = {
  id?: string;
  title?: string;
  topic?: string;
  type?: string;
  status?: string;
  current_round?: number;
  total_rounds?: number;
  spent_cents?: number;
  budget_cents?: number;
  tokens_in?: number;
  tokens_out?: number;
};

export function TopBar({
  here, goTo, role, onRoleChange, meetingType, roomLayout, onLayoutChange,
  meeting, connected,
}: {
  here?: string;
  goTo?: (id: string) => void;
  role?: Role;
  onRoleChange?: (r: Role) => void;
  meetingType?: string;
  roomLayout?: RoomLayout;
  onLayoutChange?: (l: RoomLayout) => void;
  meeting?: MeetingHeader;
  connected?: boolean;
}) {
  const tabs = [
    { id: 'agenda',     label: 'Agenda',     sub: 'r5' },
    { id: 'room',       label: 'Room',       sub: 'live' },
    { id: 'resolution', label: 'Resolution', sub: 'vote' },
    { id: 'replay',     label: 'Replay',     sub: 'fork' },
    { id: 'studio',     label: 'Studio',     sub: 'agents' },
    { id: 'meetings',   label: 'Meetings',   sub: 'history' },
  ];
  const breadcrumb =
    meeting?.title || (meeting?.id ? meeting.id.slice(0, 8) : '— no meeting —');
  return (
    <>
      <div className="term-bar">
        <div className="lights"><span /><span /><span /></div>
        <div className="crumbs">
          <span style={{ color: 'var(--live)', fontWeight: 600 }}>◆ AgentMeeting</span>
          <span className="sep">/</span>
          <span>{breadcrumb}</span>
          <span className="sep">/</span>
          <span className="here">{here}</span>
        </div>
        <div className="right">
          <CostMeter
            tokens={(meeting?.tokens_in || 0) + (meeting?.tokens_out || 0)}
            cents={meeting?.spent_cents || 0}
            budget={meeting?.budget_cents || 500}
          />
          {onRoleChange && role && <RoleSwitcher role={role} onChange={onRoleChange} compact />}
        </div>
      </div>
      <div className="sliver">
        {tabs.map(t => (
          <button key={t.id}
            className={here === t.id ? 'active' : ''}
            onClick={() => goTo && goTo(t.id)}>
            <span>{t.label}</span>
            <span style={{ color: 'var(--fg-3)' }}>· {t.sub}</span>
          </button>
        ))}
        {here === 'room' && onLayoutChange && (
          <div className="flex" style={{
            marginLeft: 14,
            border: '1px solid var(--line-2)', borderRadius: 100,
            padding: 2, alignItems: 'center',
          }}>
            {([
              { id: 'chat-stream',  label: '⌐ stream' },
              { id: 'round-table', label: '◯ table' },
              { id: 'arena',       label: '⚑ arena' },
            ] as { id: RoomLayout; label: string }[]).map(opt => (
              <button key={opt.id} onClick={() => onLayoutChange(opt.id)}
                style={{
                  background: roomLayout === opt.id ? 'var(--bg-3)' : 'transparent',
                  color: roomLayout === opt.id ? 'var(--fg-0)' : 'var(--fg-3)',
                  border: 0, borderRadius: 100,
                  padding: '3px 10px', cursor: 'pointer',
                  fontFamily: 'var(--mono)', fontSize: 10,
                  letterSpacing: '0.04em',
                  fontWeight: roomLayout === opt.id ? 600 : 400,
                }}>{opt.label}</button>
            ))}
          </div>
        )}
        <div className="right">
          {connected || meeting?.status === 'running' ? (
            <><span className="dot live pulse" /><span className="live-fg">LIVE</span></>
          ) : meeting?.status === 'ended' ? (
            <><span className="dot" style={{ background: 'var(--fg-3)' }} /><span className="dim">ENDED</span></>
          ) : meeting?.status === 'paused' ? (
            <><span className="dot" style={{ background: 'var(--warn)' }} /><span className="warn-fg">PAUSED</span></>
          ) : meeting?.status === 'draft' ? (
            <><span className="dot" style={{ background: 'var(--fg-3)' }} /><span className="dim">DRAFT</span></>
          ) : (
            <><span className="dot" style={{ background: 'var(--fg-4)' }} /><span className="dim">OFFLINE</span></>
          )}
          <span>·</span>
          <span>r{(meeting?.current_round ?? 0) + 1}/{meeting?.total_rounds || 6}</span>
          <span>·</span>
          <span>{(meeting?.type || meetingType || 'decision').toUpperCase()}</span>
        </div>
      </div>
    </>
  );
}
